/**
 * The banners at the top of every page (the staging ribbon, the impersonation and maintenance banners) as a CSS length:
 * `--site-banners-h` on the root element, their rendered height. The full-screen play pages (Tarock's table, the tile
 * table) hide the site header but keep the banners, which stay above them in the sticky top bar; the pages add
 * `--site-banners-h` to the top safe-area inset (`--safe-top`), so the table starts below the banners. A change of
 * height (a banner appears once the config has loaded) re-measures the play page's viewport like a resize.
 */
import type { RefObject } from 'preact';
import { useLayoutEffect } from 'preact/hooks';

/** Keeps `--site-banners-h` equal to the height of `ref`'s element. */
export function useBannersHeight(ref: RefObject<HTMLElement>): void {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver !== 'function') {
      return;
    }
    let last = -1;
    const update = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      if (h !== last) {
        last = h;
        document.documentElement.style.setProperty('--site-banners-h', `${h}px`);
        // the play pages measure their viewport on resize (`watchTileViewport`, `watchViewport`)
        window.dispatchEvent(new Event('resize'));
      }
    };
    const ro = new ResizeObserver(update);
    ro.observe(el);
    update();
    return () => ro.disconnect();
  }, [ref]);
}
